// @flow
import React, {
    useState, useEffect, useRef, useCallback, useContext,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from 'grommet';
import { APIContext } from 'speed-js-react';
import { FieldWrapper } from 'speed-js-react-web';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';
import ApiForm from 'esm-web-pro/containers/forms/ApiForm';
import GreenButton from 'esm-web-pro/components/layout/GreenButton';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';

type Props = {
    handleClose: Function,
    selectedLayers: Array,
}

const GroupActionsForm = (props: Props) => {
    const {
        handleClose, selectedLayers,
    } = props;

    const { apiClient } = useContext(APIContext);

    const [groupActionChoices, setGroupActionChoices] = useState(null);
    const [groupAction, setGroupAction] = useState(null);

    const [project, setProject] = useState(null);
    const [projectStage, setProjectStage] = useState(null);
    const [choices, setChoices] = useState({});

    const formRef = useRef();

    useEffect(() => {
        apiClient.resources.networkGroupActions.list().then((response) => setGroupActionChoices(response));
    }, [apiClient]);

    useEffect(() => {
        if (project) {
            apiClient.resources.projectsStageNested.list(project).then((res) => {
                let object = {};
                res.results.forEach((result) => {
                    object = {
                        ...object,
                        [result.id]: result.name,
                    };
                });
                setChoices(object);
            });
        }
    }, [project]);

    const handleSubmit = () => {
        const { project: projectUuid, project_stage: stage } = formRef.current.state.values;
        const data = {
            project: projectUuid,
            project_stage: stage,
            layers: selectedLayers,
            serializer: groupAction,
        };
        formRef.current.resetForm();
        handleClose();
        return data;
    };

    const renderGroupActionSelect = useCallback(() => {
        if (groupActionChoices) {
            return (
                <FieldWrapper label={
                    <FormattedMessage id="infrastructure.group.actions" defaultMessage="Group actions" />
                }
                >
                    <BaseSelect
                        choices={groupActionChoices}
                        value={groupAction}
                        onChange={(newGroupAction) => {
                            setGroupAction(newGroupAction);
                        }}
                        emptyLabel={(
                            <>
                                <FormattedMessage id="empty-select-label" defaultMessage="No" />
                                {' '}
                                <FormattedMessage id="group-action" defaultMessage="Group action" />
                            </>
                        )}
                    />
                </FieldWrapper>
            );
        }
        return null;
    }, [groupAction, groupActionChoices]);

    const renderApiForm = useCallback(() => {
        if (groupAction) {
            return (
                <ApiForm
                    height="100%"
                    direction="column"
                    flex
                    justify="between"
                    forwardRef={formRef}
                    resourceName="groupActions"
                    optionsResourceArguments={[{ serializer: groupAction }]}
                    onSubmit={handleSubmit}
                    onClose={handleClose}
                    excludeFields={!project && ['project_stage']}
                    extraSchema={[{ 'project_stage.ui.choices': choices }]}
                    onValuesChanged={(values) => {
                        setProject(values.project);
                        setProjectStage(values.project_stage);
                    }}
                    actions={(
                        <ActionButtons
                            disabled={selectedLayers.length <= 0 || !project || !projectStage}
                            onClose={handleClose}
                        />
                    )}
                />
            );
        }
        return null;
    }, [groupAction, selectedLayers, project, choices]);

    const renderRedButton = useCallback(() => {
        if (!groupAction) {
            return (
                <Box pad="medium">
                    <GreenButton
                        label={(
                            <FormattedMessage
                                id="account-menu.log-out"
                                defaultMessage="Log out"
                            />
                        )}
                        onClick={handleClose}
                    />
                </Box>
            );
        }
        return null;
    }, [groupAction]);

    return (
        <Box height="100%">
            <Box
                direction="row"
                align="center"
                height="100%"
                overflow="auto"
                style={{ position: 'unset' }}
            >
                <Box alignSelf="stretch" flex justify="between">
                    <Box pad={{ horizontal: 'medium' }}>
                        {renderGroupActionSelect()}
                    </Box>
                    {renderApiForm()}
                    {renderRedButton()}
                </Box>
            </Box>
        </Box>
    );
};

export default GroupActionsForm;
